"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, Layers, Sparkles, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface OverviewCopy {
  badge: string;
  verifiedLabel: string;
  heading: string;
  lead: string;
  point1Title: string;
  point1Desc: string;
  point2Title: string;
  point2Desc: string;
  point3Title: string;
  point3Desc: string;
  ctaTechData: string;
  imgAlt: string;
}
const copyData: Record<Lang, OverviewCopy> = {
  en: {
    badge: "Hygienic Process Standards",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Sanitary Metallic Filter Media Engineered for Pure Process Integrity",
    lead: "In food and pharmaceutical manufacturing, filtration media must deliver repeatable particle retention and withstand intensive sanitary cleaning without shedding media or compromising batch purity.",
    point1Title: "Zero Media Migration & Non-Shedding",
    point1Desc: "Precision diffusion-bonded sintered laminates and compacted Dutch weaves prevent wire movement and fiber shedding into sensitive process streams.",
    point2Title: "Sanitary CIP & SIP Cleanability",
    point2Desc: "Smooth wire surfaces and crevice-free seam geometries withstand repetitive clean-in-place chemical washes and high-temperature steam sterilization.",
    point3Title: "Full Heat-Lot Traceability & Documentation",
    point3Desc: "Chemical and mechanical mill test reports (EN 10204 3.1) and bubble point integrity verification are available when specified in the order.",
    ctaTechData: "Explore Technical Resources",
    imgAlt: "Hygienic stainless steel wire mesh filtration element for sanitary processing"
  },
  zh: {
    badge: "卫生级工艺标准",
    verifiedLabel: "ISO 9044 / ASTM E2016 规范",
    heading: "全金属卫生级丝网滤材 专为高纯工艺完整性打造",
    lead: "在食品与制药制造流程中，滤材必须实现可重复的颗粒截留，并耐受高频严苛的卫生清洗，杜绝介质脱落与批次交叉污染。",
    point1Title: "零介质脱落与高精度拦截",
    point1Desc: "精密扩散粘结烧结复合网与致密荷兰密纹网杜绝纤维脱落，确保下游工艺流绝对纯净。",
    point2Title: "全面适配 CIP 在线清洗与 SIP 蒸汽灭菌",
    point2Desc: "平滑丝径表面与无死角焊接结构耐受反复强酸强碱清洗与高温高压蒸汽灭菌。",
    point3Title: "批次炉号追溯与技术文件支持",
    point3Desc: "EN 10204 3.1 材质检验报告与气泡点孔径验证报告可按订单要求提供完整档案。",
    ctaTechData: "查阅技术规格资料",
    imgAlt: "卫生级不锈钢丝网过滤元件与滤筒组件"
  },
  es: {
    badge: "Normas de procesos higiénicos",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Medios filtrantes metálicos sanitarios diseñados para integridad pura del proceso",
    lead: "En la industria alimentaria y farmacéutica, los medios filtrantes deben ofrecer retención repetible y soportar limpiezas sanitarias intensivas sin desprendimiento ni contaminación.",
    point1Title: "Cero migración de partículas y sin desprendimiento",
    point1Desc: "Laminados sinterizados rígidos y tejidos Dutch compactos previenen el desprendimiento de fibras en fluidos sensibles.",
    point2Title: "Limpieza CIP y esterilización SIP comprobadas",
    point2Desc: "Superficies lisas y soldaduras sin hendiduras que resisten lavados cáusticos repetidos y vapor a alta temperatura.",
    point3Title: "Trazabilidad por colada y documentación técnica",
    point3Desc: "Informes de ensayo de fábrica EN 10204 3.1 y pruebas de punto de burbuja disponibles cuando se especifiquen en el pedido.",
    ctaTechData: "Explorar recursos técnicos",
    imgAlt: "Elemento de filtración de malla de acero inoxidable higiénico para procesamiento sanitario"
  },
  de: {
    badge: "Hygienische Prozessstandards",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Sanitäre metallische Filtermedien für unübertroffene Prozessreinheit",
    lead: "In der Lebensmittel- und Pharmaindustrie müssen Filtermedien reproduzierbare Partikelrückhaltung bieten und intensiven Reinigungsprozessen ohne Partikelabrieb standhalten.",
    point1Title: "Keine Medienmigration & partikelfrei",
    point1Desc: "Präzisions-Sinterverbunde und dichte Tressengewebe verhindern jeglichen Fasereintrag in anspruchsvolle Produktströme.",
    point2Title: "CIP-reinigbar und SIP-dampfsterilisierbar",
    point2Desc: "Glatte Drahtoberflächen und spaltfreie Schweißnähte halten zyklischen chemischen Spülungen und Heißdampf stand.",
    point3Title: "Schmelzenrückverfolgung & Dokumentation",
    point3Desc: "EN 10204 3.1 Abnahmeprüfzeugnisse und Blaspunkt-Prüfprotokolle sind bei entsprechender Vereinbarung im Auftrag verfügbar.",
    ctaTechData: "Technische Ressourcen ansehen",
    imgAlt: "Hygienisches Edelstahl-Drahtgewebe-Filterelement für Sanitärprozesse"
  },
  fr: {
    badge: "Standards de procédés hygiéniques",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Médias filtrants métalliques sanitaires conçus pour l'intégrité des procédés",
    lead: "En production agroalimentaire et pharmaceutique, les toiles filtrantes doivent garantir une coupure reproductible et tolérer des nettoyages intensifs sans relargage de fibres.",
    point1Title: "Zéro migration de média et sans relargage",
    point1Desc: "Laminés frittés par diffusion et toiles hollandaises compactes éliminant tout relargage de particules dans les fluides critiques.",
    point2Title: "Nettoyabilité NEP (CIP) et stérilisation SEP (SIP)",
    point2Desc: "Fils aux surfaces lisses et soudures sans recoins résistant aux lavages chimiques et à la vapeur sous pression.",
    point3Title: "Traçabilité des coulées et dossiers techniques",
    point3Desc: "Certificats d'essais EN 10204 3.1 et vérifications d'intégrité au point de bulle disponibles si spécifiés à la commande.",
    ctaTechData: "Consulter les ressources techniques",
    imgAlt: "Élément filtrant en toile inox sanitaire pour procédés agroalimentaires et pharmaceutiques"
  },
  pt: {
    badge: "Padrões de processos higiênicos",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Meios filtrantes metálicos sanitários projetados para integridade de processo",
    lead: "Na fabricação de alimentos e medicamentos, os meios filtrantes devem oferecer retenção reprodutível e suportar limpezas intensivas sem desprendimento de meio.",
    point1Title: "Zero migração de meio e sem desprendimento",
    point1Desc: "Laminados sinterizados rígidos e tramas holandesas compactas evitam desprendimento de fibras em fluxos sensíveis.",
    point2Title: "Compatibilidade sanitária CIP e esterilização SIP",
    point2Desc: "Superfícies de arame lisas e soldas sem frestas resistem a lavagens químicas frequentes e vapor sanitário.",
    point3Title: "Rastreabilidade de corrida e documentação técnica",
    point3Desc: "Relatórios de ensaio de fábrica EN 10204 3.1 e verificação de ponto de bolha disponíveis sob pedido.",
    ctaTechData: "Consultar recursos técnicos",
    imgAlt: "Elemento de filtração em malha de aço inoxidável higiênico para processamento sanitário"
  }
};
export default function FoodPharmaOverview() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const keyPoints = [{
    icon: Layers,
    title: c.point1Title,
    desc: c.point1Desc
  }, {
    icon: Sparkles,
    title: c.point2Title,
    desc: c.point2Desc
  }, {
    icon: ShieldCheck,
    title: c.point3Title,
    desc: c.point3Desc
  }];
  return <section id="foodpharmaoverview" data-nav="light" className="bg-muted/50 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left 6 Columns: Large Industrial Photo (no overlay, framed) */}
          <div className="lg:col-span-6">
            <Reveal className="w-full">
              <div className="mf-media-framed mf-ratio-card w-full mf-img-zoom">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png" alt={c.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
            </Reveal>
          </div>

          {/* Right 6 Columns: Narrative & Exactly Three Key Points */}
          <Reveal delay={80} className="lg:col-span-6 flex flex-col justify-center">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm tracking-wide bg-card text-foreground">
                {c.badge}
              </Badge>
              <span className="text-sm font-mono text-muted-foreground">
                {c.verifiedLabel}
              </span>
            </div>

            <h2 className="font-default tracking-tight text-foreground mf-h2">
              {c.heading}
            </h2>

            <p className="mt-4 text-muted-foreground mf-lead max-w-[65ch]">
              {c.lead}
            </p>

            {/* Exactly Three Plain Key Points */}
            <div className="mt-6 flex flex-col gap-3.5">
              {keyPoints.map((point, index) => {
              const IconComponent = point.icon;
              return <Reveal key={point.title} delay={120 + index * 80}>
                    <div className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border">
                      <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-default font-semibold text-base text-foreground leading-snug">
                          {point.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                          {point.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>;
            })}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 border-border bg-card hover:bg-muted font-semibold">
                <Link to="/TechnicalResourcesPage">
                  {c.ctaTechData}
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}